import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IonicModule } from '@ionic/angular';

import { KnowledgeBaseCollectionsService } from 'src/services/knowledge-base-collections.service';

interface CollectionPathItem {
  id: string;
  name: string;
}

@Component({
  selector: 'app-article-location-display',
  templateUrl: './article-location-display.component.html',
  styleUrls: ['./article-location-display.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule],
})
export class ArticleLocationDisplayComponent implements OnInit, OnChanges {
  @Input() collectionId: string | null = null;
  @Input() disabled = false;
  @Output() moveRequested = new EventEmitter<MouseEvent>(); // Emit event for popover positioning

  isLoadingPath = false;
  collectionPath: CollectionPathItem[] = [];
  readonly rootLocationName = 'Knowledge Base';

  // Getter for the ARIA label path string
  get ariaLabelPathString(): string {
    if (this.isLoadingPath) {
      return 'Loading location';
    }
    if (!this.collectionPath || this.collectionPath.length === 0) {
      return this.rootLocationName;
    }
    // Join the names of all segments in the path
    return `${this.rootLocationName} / ${this.collectionPath.map((segment) => segment.name).join(' / ')}`;
  }

  constructor(private collectionsService: KnowledgeBaseCollectionsService) {}

  ngOnInit(): void {
    this.loadCollectionPath();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload path if collectionId changes
    if (changes['collectionId'] && !changes['collectionId'].firstChange) {
      this.loadCollectionPath();
    }
  }

  async loadCollectionPath(): Promise<void> {
    // Reset path if collectionId is null or undefined
    if (!this.collectionId) {
      this.collectionPath = [];
      this.isLoadingPath = false; // Ensure loading state is reset
      return;
    }

    this.isLoadingPath = true;
    try {
      // Optimization: Consider passing all collections if already fetched in parent
      this.collectionPath = await this.collectionsService.getCollectionPath(this.collectionId);
    } catch (error) {
      console.error('Failed to load collection path:', error);
      // Provide a more informative error state if possible
      this.collectionPath = [{ id: this.collectionId, name: 'Error loading path' }];
    } finally {
      this.isLoadingPath = false;
    }
  }

  onMoveClick(event: MouseEvent): void {
    if (!this.disabled) {
      event.stopPropagation(); // Prevent potential parent clicks
      this.moveRequested.emit(event);
    }
  }
}
